import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchInspections, type InspectionListItem } from '../api/inspections';
import { ca } from '../i18n/ca';
import { PageLoading, PageProblem } from './PageStates';
import { useRetryKey } from './connectionHelpers';
import { Shell } from './Shell';
import { tourReason } from './tourReason';
import { fmtMoment } from './dates';

const when = (iso: string | null) => (iso ? fmtMoment(iso) : '—');

/** Every closeup inspection tour a campaign has run ({@code /proves/inspeccions}), newest first. */
export function Inspections() {
  const t = ca.inspectionsPage;
  const [items, setItems] = useState<InspectionListItem[] | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const [attempt, retry] = useRetryKey();

  useEffect(() => {
    setFailure(null);
    fetchInspections()
      .then(setItems)
      .catch((err) => {
        setFailure(err);
        setItems(null);
      });
  }, [attempt]);

  return (
    <Shell title={t.title} width="wide">
      <p>
        <Link to="/proves">← {ca.testsPage.back}</Link>
      </p>
      <p>{t.intro}</p>
      <p>
        <Link to="/proves/inspeccions/resum">{ca.inspectionSummaryPage.title}</Link>
      </p>
      {items === null && failure === null && <PageLoading />}
      {failure !== null && <PageProblem error={failure} onRetry={retry} />}
      {items && items.length === 0 && <p className="muted small">{t.none}</p>}
      <ul className="card-list inspection-list">
        {items?.map((i) => (
          <li key={i.sessionCode} className="card">
            {i.previews.length > 0 && (
              <Link
                to={`/proves/inspeccio/${i.sessionCode}`}
                className="inspection-previews"
                tabIndex={-1}
              >
                {i.previews.map((url) => (
                  <img key={url} src={url} alt="" loading="lazy" />
                ))}
              </Link>
            )}
            <h3>{t.recipeWorld(i.recipeTitle, i.worldClass)}</h3>
            <p className="muted small">
              {when(i.date)}
              {i.tour?.platformVersion
                ? ` · ${ca.inspectionReviewPage.tour.version(i.tour.platformVersion)}`
                : ''}
            </p>
            {i.tour && (
              <p>
                <span
                  className={`chip small ${i.tour.tray === 'done' ? 'ok' : i.tour.tray === 'review' ? 'warn' : ''}`}
                >
                  {ca.inspectionReviewPage.tour.states[i.tour.state] ?? i.tour.state}
                </span>{' '}
                {i.tour.tray === 'skip' && (
                  <span className="muted small">{tourReason(i.tour)}</span>
                )}
              </p>
            )}
            <p>
              {i.captures === 0 ? t.noCaptures : t.captures(i.captures)}
              {i.captures > 0 && ` · ${t.reviewed(i.reviewed, i.captures)}`}
              {i.withheld > 0 && ` · ${t.withheld(i.withheld)}`}
            </p>
            <p>
              <Link to={`/proves/inspeccio/${i.sessionCode}`}>{t.open}</Link>
              {i.campaignId != null && (
                <>
                  {' · '}
                  <Link to={`/proves/inspeccions/campanya/${i.campaignId}`}>{t.openCampaign}</Link>
                </>
              )}
            </p>
          </li>
        ))}
      </ul>
    </Shell>
  );
}
